import { Link } from "react-router-dom";
import { useIncidents } from "@crm/api";
import { Plus, AlertCircle, Clock, CheckCircle } from "lucide-react";
import { cn } from "@crm/utils";

const statusConfig: Record<string, { label: string; color: string; icon: typeof AlertCircle }> = {
  new: { label: "New", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
  assigned: { label: "Assigned", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-800", icon: Clock },
  on_hold: { label: "On Hold", color: "bg-gray-100 text-gray-800", icon: Clock },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-800", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-green-100 text-green-800", icon: CheckCircle },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-gray-100 text-gray-700" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-700" },
  high: { label: "High", color: "bg-orange-100 text-orange-700" },
  critical: { label: "Critical", color: "bg-red-100 text-red-700" },
};

export function IncidentsListPage() {
  const { data, isLoading, error } = useIncidents();

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Incidents</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage and track all service desk tickets
          </p>
        </div>
        <Link
          to="/incidents/new"
          className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          New Incident
        </Link>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-8 text-center">
          <p className="text-destructive">Failed to load incidents</p>
        </div>
      ) : !data?.results?.length ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">
            No incidents yet. Create your first incident to get started.
          </p>
          <Link
            to="/incidents/new"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <Plus className="h-4 w-4" />
            Create Incident
          </Link>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border text-left text-sm font-medium text-muted-foreground">
                <th className="p-4">Title</th>
                <th className="p-4">Status</th>
                <th className="p-4">Priority</th>
                <th className="p-4">Assigned To</th>
                <th className="p-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((incident) => {
                const status = statusConfig[incident.status] || statusConfig.new;
                const priority = priorityConfig[incident.priority] || priorityConfig.medium;
                return (
                  <tr
                    key={incident.id}
                    className="border-b border-border last:border-0 hover:bg-muted/50"
                  >
                    <td className="p-4">
                      <Link
                        to={`/incidents/${incident.id}`}
                        className="font-medium text-foreground hover:text-primary"
                      >
                        {incident.title}
                      </Link>
                    </td>
                    <td className="p-4">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
                          status.color
                        )}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="p-4">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                          priority.color
                        )}
                      >
                        {priority.label}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {incident.assigned_to
                        ? `${incident.assigned_to.first_name} ${incident.assigned_to.last_name}`
                        : "Unassigned"}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(incident.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
